// ─── Editor Preview Page ──────────────────────────────────────
// Standalone page rendered inside an iframe in the editor.
// Receives template data via postMessage and renders TemplateRenderer.
// Has its own viewport → CSS media queries work correctly for mobile.

import React, { useState, useEffect } from 'react';
import TemplateRenderer from '../components/renderer/TemplateRenderer';
import { CollageEditContext } from '../components/collage/CollageEditContext';

export default function EditorPreviewPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    // Tighten origin: only accept/send messages from our own origin
    const TRUSTED_ORIGIN = window.location.origin;
    const handleMessage = (event) => {
      if (event.origin !== TRUSTED_ORIGIN) return;
      if (event.data?.type === 'EDITOR_PREVIEW_UPDATE') {
        setData(event.data.payload);
      }
    };
    window.addEventListener('message', handleMessage);
    // Signal parent that preview is ready (same-origin only)
    window.parent?.postMessage({ type: 'EDITOR_PREVIEW_READY' }, TRUSTED_ORIGIN);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-pulse text-xs text-gray-400">Loading preview...</div>
      </div>
    );
  }

  const { templateConfig, invitationData, themeOverrides, slug, isCollage, editCallbacks } = data;

  // For collage templates, provide a read-only edit context with mobile layout mode
  const collageCtx = isCollage ? {
    onElementMove: () => {},
    onElementResize: () => {},
    onElementRotate: () => {},
    selectedElement: data.selectedElement || null,
    onSelectElement: (sectionId, elementId) => {
      // Notify parent about selection (same-origin only)
      window.parent?.postMessage({
        type: 'EDITOR_PREVIEW_SELECT',
        payload: { sectionId, elementId },
      }, window.location.origin);
    },
    layoutMode: 'mobile',
  } : null;

  const content = (
    <TemplateRenderer
      templateConfig={templateConfig}
      invitationData={invitationData}
      themeOverrides={themeOverrides}
      slug={slug}
    />
  );

  return isCollage ? (
    <CollageEditContext.Provider value={collageCtx}>
      {content}
    </CollageEditContext.Provider>
  ) : content;
}
